import type { OmitIndexSignature } from '@eturi/ts-helpers'
import { isEnter } from '@op/react-web'
import cls from 'classnames'
import type { ChangeEvent, FocusEvent } from 'react'
import { memo, type SelectHTMLAttributes } from 'react'
import type { FieldRenderProps } from 'react-final-form'
import { Field, type FieldProps } from 'react-final-form'
import { FieldError, FormLabel, hasFieldError } from '../../widgets'

type SelectInputProps = SelectHTMLAttributes<HTMLSelectElement> & {
	readonly 'data-testid'?: string
}

export type SelectFieldItem = {
	readonly label: string
	readonly value: string
}

export type SelectFieldProps = OmitIndexSignature<FieldProps<string, any, HTMLSelectElement>> &
	SelectInputProps & {
		readonly emptyValue?: string
		readonly fieldClassName?: string
		readonly options: SelectFieldItem[]
		readonly label?: string
		readonly labelClassName?: string
		readonly name: string
	}

export const SelectField = memo(
	({
		className,
		emptyValue = '',
		fieldClassName,
		initialValue,
		label,
		labelClassName,
		options,
		validate,
		...restProps
	}: SelectFieldProps) => (
		<Field {...restProps} initialValue={initialValue} validate={validate}>
			{({ input, meta }: FieldRenderProps<string, HTMLSelectElement>) => {
				const passthroughEvent: PassthroughEvent = (eventName) => (ev) => {
					input[eventName](ev)
					restProps[eventName]?.(ev as any)
				}

				const SelectNode = (
					<select
						{...input}
						{...restProps}
						className={cls(
							'select',
							input.value === emptyValue && '!text-placeholder',
							!label && '!text-left ![text-align-last:left]',
							className,
						)}
						onBlur={passthroughEvent('onBlur')}
						onChange={passthroughEvent('onChange')}
						onFocus={passthroughEvent('onFocus')}
						onKeyDown={(ev) => {
							// Fix issue where pressing enter while this field is focused would submit the form
							if (isEnter(ev)) ev.preventDefault()
							restProps.onKeyDown?.(ev)
						}}
					>
						{options.map((item) => (
							<option key={item.value} value={item.value}>
								{item.label}
							</option>
						))}
					</select>
				)

				return (
					<div className={cls('field', { error: hasFieldError(meta) }, fieldClassName)}>
						{label != null ?
							<FormLabel label={label} className={labelClassName}>
								{SelectNode}
							</FormLabel>
						:	SelectNode}

						<FieldError meta={meta} />
					</div>
				)
			}}
		</Field>
	),
)

SelectField.displayName = 'SelectField'

type PassthroughEvent = {
	(eventName: 'onBlur'): (ev: FocusEvent<HTMLSelectElement>) => void
	(eventName: 'onChange'): (ev: ChangeEvent<HTMLSelectElement>) => void
	(eventName: 'onFocus'): (ev: FocusEvent<HTMLSelectElement>) => void
}
